import { createSelector } from "@reduxjs/toolkit";
import { selectProjectNo } from "../sharedSelectors";
import { IRootState } from "../app/store";
import { IRoomsSummaryHashmap } from "@backend/src/shared/schemas/schemas";
import { openapi } from "../../openapi";
import { selectUniqueMcbimSpaceNames } from "../McBim/extraMcBimSelectors";
import { selectSelectedPnsIds } from "../DesignTab/routing/pnsSelectionSlice";

const EMPTY_ROOMS_CURRENT_FLOOR: IRoomsSummaryHashmap = {};

export const selectedRoomNr = (state: IRootState) => state.design.selectedSpaceNumber;
// Args för att hämta alla rooms (används av roomSelectors.ts)
export const selectGetRoomsArgs = createSelector(
  [selectUniqueMcbimSpaceNames, selectProjectNo],
  (uniqueNames, projectNo) => {
    if (uniqueNames.length === 0 || !projectNo) {
      return null;
    }
    return {
      body: {
        projectNo: projectNo,
        roomNumbers: uniqueNames,
      },
    };
  }
);

export const selectHttpGetRoomsCurrentFloorResult = (state: IRootState): IRoomsSummaryHashmap => {
  const args = selectGetRoomsArgs(state);

  if (!args) return EMPTY_ROOMS_CURRENT_FLOOR;

  return (
    openapi.endpoints.postMultiplacerRooms.select(args)(state).data ?? EMPTY_ROOMS_CURRENT_FLOOR
  );
};

export const selectHttpSingleRoomFull = createSelector(
  [selectProjectNo, selectedRoomNr, (state: IRootState) => state],
  (projectNo, roomNr, state) => {
    if (!projectNo || !roomNr) return null;

    const args = {
      projectNo: Number(projectNo),
      roomNumber: roomNr,
    };

    return openapi.endpoints.getMultiplacerSingleRoom.select(args)(state).data ?? null;
  }
);

const EMPTY_ROOMS: IRoomsSummaryHashmap = {};

export const selectSelectedPnsPrefixesForRooms = createSelector(
  [selectSelectedPnsIds],
  (selectedIds): string[] => Array.from(new Set(selectedIds.map((id) => id.trim()).filter(Boolean)))
);

/**
 * Hämtar alla rooms på projektet (utan filtrering på McBIM-space)
 */
export const selectHttpGetAllRoomsOnProjectResult = createSelector(
  [selectProjectNo, (state: IRootState) => state],
  (projectNo, state): IRoomsSummaryHashmap => {
    if (!projectNo) return EMPTY_ROOMS;

    const args = {
      body: {
        projectNo,
      },
    };

    return (
      openapi.endpoints.postMultiplacerRooms.select(args)(state).data ??
      (EMPTY_ROOMS as IRoomsSummaryHashmap)
    );
  }
);

export const selectGetAllRoomsWithPnsArgs = createSelector(
  [selectProjectNo, selectSelectedPnsPrefixesForRooms],
  (projectNo, pnsPrefixes) => {
    if (!projectNo) return null;

    return {
      body: {
        projectNo,
        ...(pnsPrefixes.length > 0 ? { pns: pnsPrefixes } : {}),
      },
    };
  }
);

export const selectHttpGetAllRoomsOnProjectWithPnsFilterResult = createSelector(
  [selectGetAllRoomsWithPnsArgs, (state: IRootState) => state],
  (args, state): IRoomsSummaryHashmap => {
    if (!args) return EMPTY_ROOMS;

    return openapi.endpoints.postMultiplacerRooms.select(args)(state).data ?? EMPTY_ROOMS;
  }
);

const selectActiveProjectTab = (state: IRootState) => state.global.activeProjectTab;
export const selectHttpRoomsBasedOnActiveProjectTab = createSelector(
  [
    selectActiveProjectTab,
    selectHttpGetAllRoomsOnProjectWithPnsFilterResult,
    selectHttpGetRoomsCurrentFloorResult,
  ],
  (activeTab, pnsFiltredRooms, currentFloorRooms): IRoomsSummaryHashmap => {
    if (activeTab === "design") {
      return currentFloorRooms;
    }
    return pnsFiltredRooms;
  }
);
